import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix für Leaflet Marker Icons (werden sonst nicht angezeigt)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Komponente um Karte zu zentrieren wenn sich Koordinaten ändern
function MapController({ center, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
}

// Komponente für Klick-Events auf der Karte
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      if (onLocationSelect) {
        onLocationSelect(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

/**
 * GPS-Karte für Standort-Auswahl
 * @param {number} latitude - Breitengrad
 * @param {number} longitude - Längengrad
 * @param {number} radius - Radius in Metern
 * @param {function} onLocationSelect - Callback wenn auf Karte geklickt wird (lat, lng)
 * @param {boolean} editable - Ob Klick auf Karte erlaubt ist
 */
export function LocationMap({ 
  latitude, 
  longitude, 
  radius = 100, 
  onLocationSelect, 
  editable = true,
  className = ""
}) {
  // Standard-Position: Deutschland-Mitte wenn keine Koordinaten
  const defaultCenter = [51.1657, 10.4515];
  const hasValidCoords = latitude && longitude && !isNaN(latitude) && !isNaN(longitude);
  const center = hasValidCoords ? [parseFloat(latitude), parseFloat(longitude)] : defaultCenter;
  const zoom = hasValidCoords ? 16 : 6;

  return (
    <div className={`relative rounded-lg overflow-hidden border ${className}`} style={{ height: '200px' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapController center={center} zoom={zoom} />
        
        {editable && <MapClickHandler onLocationSelect={onLocationSelect} />}
        
        {hasValidCoords && (
          <>
            <Marker position={center} />
            <Circle 
              center={center} 
              radius={radius || 100}
              pathOptions={{ 
                color: '#22c55e', 
                fillColor: '#22c55e', 
                fillOpacity: 0.2,
                weight: 2
              }} 
            />
          </>
        )}
      </MapContainer>
      
      {/* Overlay mit Anleitung */}
      {editable && !hasValidCoords && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
          <div className="bg-white/90 px-4 py-2 rounded-lg text-sm text-center">
            <p className="font-medium">Klicken Sie auf die Karte</p>
            <p className="text-muted-foreground text-xs">um den Standort zu setzen</p>
          </div>
        </div>
      )}
      
      {/* Koordinaten-Anzeige */}
      {hasValidCoords && (
        <div className="absolute bottom-2 left-2 bg-white/90 px-2 py-1 rounded text-xs font-mono z-[1000]">
          {parseFloat(latitude).toFixed(6)}, {parseFloat(longitude).toFixed(6)}
        </div>
      )}
    </div>
  );
}

export default LocationMap;
